<template>
    <v-container :fill-height="!loaded" column fluid class="order-details-page">
        <template v-if="loaded">
            <breadcrumbs v-if="!isMobile" class="mb-4" />
            <order-details
                ref="order"
                @cancel-service="loadOrder"
                @modify-service="loadOrder"
                @issued-invoice="issuedInvoice"
                @issued-ticket="loadOrder"
                @load-order="loadOrder"
                @show-on-map="showOnMap"
            />
        </template>
        <v-layout v-else class="align-center justify-center">
            <v-progress-circular indeterminate size="64" color="primary" />
        </v-layout>
        <v-dialog v-if="isShowConfirmationDialog" v-model="confirmationStatusModal" :scrim="false" width="500px">
            <v-card>
                <v-card-text class="pt-5">
                    <confirmation-status v-if="bookingConfirmation === 'order'" :company="company" />
                    <invoice-payment-status v-if="bookingConfirmation === 'invoice'" :order-id="$route.params.id" />
                </v-card-text>
                <v-divider />
                <v-card-actions>
                    <v-btn color="primary" class="mx-auto" variant="text" @click="closeConfirmationModal">OK</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <order-service-book-modal />
        <send-email-modal />
        <contact-details-modal />
        <client-only>
            <v-dialog v-model="mapModal" :max-width="500">
                <map-location :latitude="mapPoint.latitude" :longitude="mapPoint.longitude" :height="`500px`" />
            </v-dialog>
        </client-only>
    </v-container>
</template>

<script lang="ts">
    import {Component, Vue, Hook} from 'vue-facing-decorator'
    import Breadcrumbs from '~src/components/Breadcrumbs.vue'
    import ConfirmationStatus from '~src/components/booking/confirmationStatus.src'
    import OrderDetails from '~src/components/account/orderDetails.src'
    import {CHANGE_LOCALE_EVENT, EventBus, ORDER_SERVICE_BOOKED, SHOW_INVOICES_MODAL_EVENT} from '@/utils/event-bus'
    import InvoicePaymentStatus from '@/components/booking/InvoicePaymentStatus.vue'
    import OrderServiceBookModal from '@/components/account/OrderServiceBookModal.vue'
    import SendEmailModal from '~src/components/account/modals/SendEmailModal.vue'
    import ContactDetailsModal from '@/components/account/modals/ContactDetailsModal.vue'
    import MapLocation from '~src/components/info/mapLocation.src'
    import {useRecentViewsData} from '~/composables/recent-views'

    async function showConfirmationModal() {
        try {
            const rs = await this.$api.companies.get({relationship: this.relationship})
            this.company = rs.companies[0]
            this.bookingConfirmation = 'order'
            this.confirmationStatusModal = true
            // eslint-disable-next-line no-empty
        } catch (e) {}
    }

    @Component({
        components: {
            SendEmailModal,
            OrderServiceBookModal,
            InvoicePaymentStatus,
            ConfirmationStatus,
            OrderDetails,
            Breadcrumbs,
            ContactDetailsModal,
            MapLocation,
        },
    })
    export class OrderDetailsPage extends Vue {
        confirmationStatusModal = false
        company = {
            phones: [],
        }
        bookingConfirmation = 'order'
        loaded = false

        mapModal = false
        mapPoint = {}

        beforeUnmount() {
            EventBus.$off(CHANGE_LOCALE_EVENT, this.loadOrder)
            EventBus.$off(ORDER_SERVICE_BOOKED, this.serviceBookConfirmation)
            if (!persistentStore.selectedOrderId) {
                persistentStore.SET_TOURISTS([])
                hotelsStore.SET_TOURISTS([])
            }
        }

        async mounted() {
            EventBus.$on(CHANGE_LOCALE_EVENT, this.loadOrder)
            EventBus.$on(ORDER_SERVICE_BOOKED, this.serviceBookConfirmation)
            await this.$store.restored
            if (persistentStore.selectedOrderId) {
                persistentStore.SET_SELECTED_ORDER_ID(null)
            }
            await this.loadOrder()
            const order = runtimeStore.orderDetails
            if (order) {
                const $route = useRoute()
                const linkName = order.title ? `${order.orderId} ${order.title}` : `${order.orderId}`
                await useRecentViewsData($route, linkName)
            }
        }

        @Hook()
        beforeRouteEnter(to, from, next) {
            if (from.name && (from.name === 'paymentComplete' || from.name.indexOf('Booking') !== -1)) {
                next(async vm => await showConfirmationModal.call(vm))
            } else if (from.name && from.name === 'invoicePaymentComplete') {
                next(async vm => {
                    vm.bookingConfirmation = 'invoice'
                    vm.confirmationStatusModal = true
                })
            } else {
                next()
            }
        }

        async serviceBookConfirmation() {
            await this.loadOrder()
            await showConfirmationModal.call(this)
        }

        async loadOrder() {
            this.loaded = false
            const result = await runtimeStore.loadOrder({
                orderType: authStore.orderType,
                orderId: this.$route.params.id,
            })

            // TODO [P1] Need check
            if (result?.status === 500 && result?.msg === 'Network Error') {
                throw createError({
                    statusCode: result.status,
                    message: this.$t('error_message.network_error'),
                    // note: this.$t('notes.need_internet_connection'),
                })
            } else if (!result) {
                throw createError({statusCode: 404})
            }
            this.loaded = true
        }

        async issuedInvoice(invoiceType) {
            await this.loadOrder()
            const order = runtimeStore.orderDetails
            EventBus.$emit(SHOW_INVOICES_MODAL_EVENT, {
                orderId: order.orderId,
                objectId: order.objectId,
                invoiceType: invoiceType,
                order,
            })
        }

        closeConfirmationModal() {
            this.confirmationStatusModal = false

            persistentStore.SET_BOOK_RESPONSE([
                {
                    orderId: null,
                    refNumber: null,
                    status: null,
                },
            ])
        }

        showOnMap($event) {
            this.mapPoint = $event
            this.mapModal = true
        }

        get isMobile() {
            return this.$breakpoint.smAndDown
        }

        get isShowConfirmationDialog() {
            return !!this.bookResponsePersistent?.orderId
        }

        get bookResponsePersistent() {
            return persistentStore.bookResponse[0]
        }

        get relationship() {
            return authStore.isCorporate || authStore.isTO2 || authStore.isAgency ? 'SUPPLIER' : 'OWN'
        }
    }

    export default OrderDetailsPage
</script>
